/**
 * O apelido de um jogador, como os DOIS lados o validam.
 *
 * Fica no protocolo pelo mesmo motivo da senha da sala: o cliente recusa cedo,
 * com a frase certa, e o servidor recusa de novo porque não confia no cliente.
 * Se cada lado tivesse a sua regra, um apelido aceito na tela cairia no
 * servidor com um erro que a tela não sabe explicar.
 *
 * O apelido NÃO é único. Dois jogadores podem se chamar "Zé". Por isso toda
 * exibição mostra o endereço abreviado junto — a identidade é a carteira; o
 * apelido é só o que se lê. É isso que impede alguém de se passar por outro
 * escolhendo o mesmo nome.
 */

export const NICK_MIN = 2
export const NICK_MAX = 20

/** Letras e números de qualquer alfabeto, espaço, underscore e hífen. Nada de controle. */
const PERMITIDO = /^[\p{L}\p{N} _-]+$/u

/**
 * Curta de propósito: papéis que confundem ("admin", "suporte") e o pior do
 * palavrão. Não é filtro de tudo — é o suficiente para ninguém abrir o lobby
 * e ver um "Moderador" pedindo a senha da mesa. Comparada por PALAVRA inteira
 * (separada por espaço, `_` ou `-`) e pelo apelido sem separadores, em
 * minúsculas: "Ad-min" e "admin" caem; "Adminho" não, porque é outra palavra.
 */
export const PALAVRAS_PROIBIDAS: readonly string[] = [
  'admin',
  'administrador',
  'administrator',
  'moderador',
  'moderator',
  'suporte',
  'support',
  'sistema',
  'system',
  'servidor',
  'server',
  'oficial',
  'official',
  'solpool',
  'staff',
  'bot',
  'puta',
  'caralho',
  'porra',
  'merda',
  'viado',
  'fuck',
  'shit',
  'bitch',
  'nigger',
  'cunt',
]

/**
 * NFC, sem espaço nas pontas e sem espaço repetido no meio: "Zé  da Silva"
 * e "Zé da Silva" são o mesmo apelido, e "é" digitado de dois jeitos também.
 */
export const normalizarApelido = (texto: string): string =>
  texto.normalize('NFC').trim().replace(/\s+/g, ' ')

export type ApelidoRecusa = 'curto' | 'longo' | 'caracteres' | 'proibido'

export function conferirApelido(texto: string): { ok: true; apelido: string } | { ok: false; motivo: ApelidoRecusa } {
  const apelido = normalizarApelido(texto)
  // `[...x].length` conta caracteres, não code units: um acento composto ou um
  // caractere fora do plano básico vale um.
  const tamanho = [...apelido].length
  if (tamanho < NICK_MIN) return { ok: false, motivo: 'curto' }
  if (tamanho > NICK_MAX) return { ok: false, motivo: 'longo' }
  if (!PERMITIDO.test(apelido)) return { ok: false, motivo: 'caracteres' }
  if (proibido(apelido)) return { ok: false, motivo: 'proibido' }
  return { ok: true, apelido }
}

function proibido(apelido: string): boolean {
  const baixo = apelido.toLowerCase()
  const palavras = baixo.split(/[\s_-]+/).filter((p) => p.length > 0)
  const junto = baixo.replace(/[\s_-]+/g, '')
  return PALAVRAS_PROIBIDAS.some((p) => palavras.includes(p) || junto === p)
}
